'use client';

import {
  RefObject,
  useEffect,
  useLayoutEffect,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * Tracks the viewport rect of `ref.current` while `open` is true. Returns
 * `null` when closed or before the first measurement so callers can skip
 * rendering the popover until the anchor has been positioned.
 *
 * Updates on window resize and on any scroll within the page (capture
 * phase) so popovers follow their anchor when the user pans the workspace
 * canvas, scrolls a panel, etc.
 */
export function useAnchorRect<T extends HTMLElement>(
  open: boolean,
  ref: RefObject<T | null>,
): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setRect(null);
      return;
    }
    const measure = () => {
      const node = ref.current;
      if (!node) return;
      setRect(node.getBoundingClientRect());
    };
    measure();
    window.addEventListener('resize', measure);
    // Capture-phase scroll so it fires for nested scrollable parents too.
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, ref]);

  return rect;
}

/**
 * Elements that draw no children of their own: a portal into one of them
 * would render nothing, so the host falls back to `<body>`.
 */
const REPLACED_ELEMENTS = new Set(['IFRAME', 'VIDEO', 'AUDIO', 'IMG', 'CANVAS', 'EMBED', 'OBJECT']);

/**
 * Where a popover has to be rendered to be SEEN (lane B, 2026-09-29).
 *
 * While an element is the browser's full-screen element, the browser draws
 * that element's subtree in the top layer over a black `::backdrop`, and
 * nothing else in the document: a popover portaled to `<body>` is rendered
 * and focusable, but hidden behind the backdrop. Desktop full screen presents
 * an app window's React Flow node exactly that way (maximize-app-node.ts), so
 * the ad slot's "Why this ad" panel and the Share dialog opened from its app
 * bar opened unseen. Inside the full-screen element they are drawn; the UA
 * sheet gives that element `transform: none`, so a `position: fixed` popover
 * inside it is still placed against the viewport.
 *
 * `<body>` otherwise, and also when the full-screen element is the root or a
 * replaced element (an app's own iframe gone full screen, a video): the first
 * already contains `<body>`, the second cannot hold children. Exported for
 * its test.
 */
export function popoverHost(doc: Document = document): HTMLElement {
  const d = doc as Document & { webkitFullscreenElement?: Element | null };
  const el = d.fullscreenElement ?? d.webkitFullscreenElement ?? null;
  if (
    el &&
    el !== doc.documentElement &&
    el !== doc.body &&
    typeof (el as HTMLElement).appendChild === 'function' &&
    !REPLACED_ELEMENTS.has(el.tagName.toUpperCase())
  ) {
    return el as HTMLElement;
  }
  return doc.body;
}

/**
 * Render `children` into `document.body` via React Portal so menus and
 * popovers escape any positioned/z-indexed ancestor stacking context.
 * While the browser shows an element full screen, into that element
 * instead (see `popoverHost`); a change of full-screen state re-renders the
 * portal into the right host.
 *
 * SSR safe: returns `null` until mounted on the client. Pass an explicit
 * `style` to position the floating element with `position: fixed` and a
 * high z-index (typically >= 200 so we sit above workspace edge panels).
 */
export function PopoverPortal({
  children,
  style,
  className,
  role = 'dialog',
  ariaLabel,
}: {
  children: React.ReactNode;
  style?: CSSProperties;
  className?: string;
  role?: string;
  ariaLabel?: string;
}) {
  const [mounted, setMounted] = useState(false);
  const [, setFullscreenVersion] = useState(0);
  useEffect(() => setMounted(true), []);
  // Entering or leaving full screen moves the host; re-render so the portal
  // follows (a popover left inside a node that stopped being full screen
  // would be positioned against that node's transform).
  useEffect(() => {
    const bump = () => setFullscreenVersion((v) => v + 1);
    document.addEventListener('fullscreenchange', bump);
    document.addEventListener('webkitfullscreenchange', bump);
    return () => {
      document.removeEventListener('fullscreenchange', bump);
      document.removeEventListener('webkitfullscreenchange', bump);
    };
  }, []);
  if (!mounted) return null;
  return createPortal(
    <div role={role} aria-label={ariaLabel} className={className} style={style}>
      {children}
    </div>,
    popoverHost(),
  );
}
